import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { applyDocumentDirection } from './utils/documentDirection';
// final-overrides.css is imported by Home.jsx so it loads with that page

// Lazy load page components for code splitting
const Home = lazy(() => import('./pages/Home'));
const Watch = lazy(() => import('./pages/Watch'));
const Contact = lazy(() => import('./pages/Contact'));
const Auditions = lazy(() => import('./pages/Auditions'));
const AuditionRole = lazy(() => import('./pages/AuditionRole'));
const AuditionsThanks = lazy(() => import('./pages/AuditionsThanks'));
const AuditionsAdmin = lazy(() => import('./pages/AuditionsAdmin'));
const Interview = lazy(() => import('./pages/Interview'));
const OrderMaterials = lazy(() => import('./pages/OrderMaterials'));
const NotFound = lazy(() => import('./pages/NotFound'));

export default function App() {
  const mainRef = React.useRef(null);
  const location = useLocation();

  React.useEffect(() => {
    // Shift focus to main content on route change for screen readers/keyboard users
    mainRef.current?.focus();
  }, [location]);

  React.useEffect(() => {
    applyDocumentDirection(localStorage.getItem('selectedLanguage'));
    const onStorage = (e) => {
      if (e.key === 'selectedLanguage') applyDocumentDirection(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <main id="main-content" ref={mainRef} tabIndex={-1}>
      <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/series/ipp" element={<Navigate to="/series/ipp-adult" replace />} />
          <Route path="/series/:slug" element={<Watch />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/auditions" element={<Auditions />} />
          <Route path="/auditions/admin" element={<AuditionsAdmin />} />
          <Route path="/auditions/thanks" element={<AuditionsThanks />} />
          <Route path="/auditions/:slug" element={<AuditionRole />} />
          <Route path="/interviews/:series" element={<Interview />} />
          <Route path="/interviews/:series/:episode" element={<Interview />} />
          <Route path="/Order-Comics" element={<OrderMaterials />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
      </main>
      {/* AccessibilityPanel now triggered from Header nav button */}
    </>
  );
}

// Loading fallback component for code-split routes
function PageLoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        fontFamily: '"Prompt", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }}
    >
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #2060E8',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}
        />
        <p style={{ color: '#666', fontSize: '1.1rem', margin: 0 }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
